import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { API_ENDPOINTS } from '../shared/constants/api.constants';
import { PaginatedResponse } from '../models/product.model';
import {
  ChatMessage,
  Conversation,
  ConversationStatus,
  CreateConversationRequest,
  SendMessageRequest,
} from '../models/chat.model';

@Injectable({ providedIn: 'root' })
export class ChatService {

  constructor(private http: HttpClient) {}

  /* ─── Conversations ─── */

  listConversations(page = 1, pageSize = 20): Observable<PaginatedResponse<Conversation>> {
    const params = new HttpParams().set('page', page).set('page_size', pageSize);
    return this.http.get<PaginatedResponse<Conversation>>(API_ENDPOINTS.CHAT_CONVERSATIONS, { params });
  }

  /** Idempotent - calling again for the same vendor returns the existing conversation. */
  getOrCreateConversation(payload: CreateConversationRequest): Observable<Conversation> {
    return this.http.post<Conversation>(API_ENDPOINTS.CHAT_CONVERSATIONS, payload);
  }

  getConversation(id: string): Observable<Conversation> {
    return this.http.get<Conversation>(`${API_ENDPOINTS.CHAT_CONVERSATIONS}${id}/`);
  }

  updateConversationStatus(id: string, status: ConversationStatus): Observable<Conversation> {
    return this.http.patch<Conversation>(`${API_ENDPOINTS.CHAT_CONVERSATIONS}${id}/`, { status });
  }

  closeConversation(id: string): Observable<Conversation> {
    return this.http.post<Conversation>(`${API_ENDPOINTS.CHAT_CONVERSATIONS}${id}/close/`, {});
  }

  deleteConversation(id: string): Observable<void> {
    return this.http.delete<void>(`${API_ENDPOINTS.CHAT_CONVERSATIONS}${id}/`);
  }

  markConversationRead(id: string): Observable<{ message_ids: string[] }> {
    return this.http.post<{ message_ids: string[] }>(`${API_ENDPOINTS.CHAT_CONVERSATIONS}${id}/read/`, {});
  }

  /* ─── Messages ─── */

  listMessages(conversationId: string, page = 1, pageSize = 20): Observable<PaginatedResponse<ChatMessage>> {
    const params = new HttpParams().set('page', page).set('page_size', pageSize);
    return this.http.get<PaginatedResponse<ChatMessage>>(
      `${API_ENDPOINTS.CHAT_MESSAGES_BASE}${conversationId}/messages/`,
      { params }
    );
  }

  sendMessage(conversationId: string, payload: SendMessageRequest): Observable<ChatMessage> {
    const url = `${API_ENDPOINTS.CHAT_MESSAGES_BASE}${conversationId}/messages/`;

    if (payload.attachment) {
      const form = new FormData();
      form.append('message_type', payload.message_type || 'TEXT');
      if (payload.text) form.append('text', payload.text);
      if (payload.reply_to) form.append('reply_to', payload.reply_to);
      form.append('attachment', payload.attachment);
      return this.http.post<ChatMessage>(url, form);
    }

    return this.http.post<ChatMessage>(url, {
      message_type: payload.message_type || 'TEXT',
      text: payload.text,
      reply_to: payload.reply_to,
    });
  }

  editMessage(messageId: string, text: string): Observable<ChatMessage> {
    return this.http.patch<ChatMessage>(`${API_ENDPOINTS.CHAT_MESSAGE_DETAIL_BASE}${messageId}/`, { text });
  }

  deleteMessage(messageId: string): Observable<void> {
    return this.http.delete<void>(`${API_ENDPOINTS.CHAT_MESSAGE_DETAIL_BASE}${messageId}/`);
  }

  markMessageRead(messageId: string): Observable<ChatMessage> {
    return this.http.patch<ChatMessage>(`${API_ENDPOINTS.CHAT_MESSAGE_DETAIL_BASE}${messageId}/read/`, {});
  }
}
